import { evalCondition, notifyParent } from 'mailup-react-helpers'

import styled from '@emotion/styled'

import { pageState } from 'State/page'
import {
  Actions,
  DataListHeader,
  DataListRow,
  Flex,
  Grid,
  GridCell,
  IconWarningTriangle,
  LINK_VARIANT_EXTRALIGHT,
  LINK_VARIANT_HIGHLIGHT,
  Link,
  Panel,
  SKELETON_LOADER_VARIANT_ONWHITE,
  SingleLoader,
  SkeletonLoader,
  TEXT_VARIANT_LIGHT,
  TEXT_VARIANT_SMALL_LIGHT,
  Text,
  Tooltip,
  TruncateAndTooltip,
} from 'mailup-web-components'
import React from 'react'
import { useIntl } from 'react-intl'
import { useParams } from 'react-router-dom'
import { useRecoilCallback, useRecoilValue } from 'recoil'
import Pagination from '../Pagination'
import '../ProfileClick.scss'
import { laodClickTableCallback } from '../recoil/callbacks/callbacks'
import {
  tableLoadingState,
  tableStateResult,
  tableStateSerach,
} from '../recoil/state/result'

const WordBreakContainer = styled.div`
  word-break: break-word;
`

const ClickTable = () => {
  const intl = useIntl()
  const laodClickTable = useRecoilCallback(laodClickTableCallback, [])
  const { orderBy, orderDescending } = useRecoilValue(tableStateSerach)
  const results = useRecoilValue(tableStateResult)
  const loading = useRecoilValue(tableLoadingState)
  const pageStateValue = useRecoilValue(pageState)

  const hasStatPermission = evalCondition('uo:STAT')
  const { idRecipient } = useParams()

  const onClickOrder = (data) => {
    laodClickTable({
      idRecipient,
      orderBy: data,
    })
  }

  function getActionItems(item) {
    const actions = []
    if (hasStatPermission) {
      actions.push({
        label: intl.formatMessage({ id: 'Load.Statistics.LinkReport' }),
        onClick: () =>
          notifyParent('redirectMainFrame', [
            `statistics_app_redirector.aspx?r=/stats/sendouts/link/mode/messages/0/${item.IdMessage}`,
          ]),
        className: 'test-actionMenu-LinkReport',
      })
      actions.push({
        label: intl.formatMessage({ id: 'Load.Statistics.ActivityReport' }),
        onClick: () =>
          notifyParent('redirectMainFrame', [
            `statistics_app_redirector.aspx?r=/stats/sendouts/sending/mode/messages/0/${item.IdMessage}`,
          ]),
        className: 'test-actionMenu-ActivityReport',
      })
      actions.push({
        label: intl.formatMessage({ id: 'Load.Common.Sendouts' }),
        onClick: () =>
          notifyParent('redirectMainFrame', [
            `statistics_app_redirector.aspx?r=/stats/sendouts/list/mode/sendings/msg/${item.IdMessage}`,
          ]),
        className: 'test-actionMenu-Sendouts',
      })
    }
    return actions
  }

  function setSpecialLink(specialLinkType) {
    switch (specialLinkType) {
      case 'PCN':
        return intl.formatMessage({ id: 'Load.Common.PreferenceCenterLink' })
      case 'PUF':
        return intl.formatMessage({ id: 'Load.Common.ProfileUpdateFormLink' })
      case 'S2F':
        return intl.formatMessage({ id: 'Load.Common.SendToAFriendLink' })
      case 'SHR':
        return intl.formatMessage({ id: 'Load.Common.CondivisioneSocial' })
      case 'UNS':
        return intl.formatMessage({ id: 'Load.Common.UnsubscribeLink' })
      case 'VIB':
        return intl.formatMessage({ id: 'Load.Common.ViewInBrowserLink' })
    }
  }

  //
  const heads = [
    {
      label: intl.formatMessage({ id: 'Load.MailupCommon.Message' }),
      span: hasStatPermission ? 4 : 5,
      sortable: true,
      field: 'ClickOrderbyIdMessage',
    },
    {
      label: intl.formatMessage({
        id: 'Load.ActiveRecipientClickDetails.Url',
      }),
      span: 5,
    },
    {
      label: intl.formatMessage({ id: 'Load.MailupCommon.SingleClick' }),
      span: 2,
      sortable: true,
      field: 'ClickOrderbyDate',
    },
    hasStatPermission
      ? {
          label: intl.formatMessage({ id: 'Load.MailupCommon.Actions' }),
          span: 1,
          align: 'right',
        }
      : undefined,
  ].filter((o) => o !== undefined)

  const handleMessageClick = (id) => {
    notifyParent('openPreviewMainFrame', [id])
  }

  return (
    <>
      <Tooltip />

      <Panel>
        {pageStateValue.loading && <SingleLoader height="594px" />}
        {!pageStateValue.loading && (
          <>
            <DataListHeader
              heads={heads}
              orderByStatus={orderBy}
              orderDescendingStatus={orderDescending}
              handleChangeColumnsOrderCallback={onClickOrder}
            />

            <section>
              {results.items.map((item, key) => (
                <DataListRow data-mu-test="item" key={key}>
                  <Grid cols={12}>
                    <GridCell
                      span={hasStatPermission ? 4 : 5}
                      data-mu-test="item-message"
                    >
                      {loading || !item.Subject ? (
                        <>
                          <SkeletonLoader
                            variant={SKELETON_LOADER_VARIANT_ONWHITE}
                            style={{ marginBottom: '4.5px' }}
                          />
                          <SkeletonLoader
                            variant={SKELETON_LOADER_VARIANT_ONWHITE}
                            height="14px"
                            style={{ marginBottom: '4.2px' }}
                          />
                        </>
                      ) : (
                        <>
                          <Link
                            variant={LINK_VARIANT_HIGHLIGHT}
                            data-mu-test="item-subject"
                            onClick={() => handleMessageClick(item.IdMessage)}
                          >
                            <TruncateAndTooltip text={item.Subject} />
                          </Link>

                          <Text
                            variant={TEXT_VARIANT_SMALL_LIGHT}
                            htmlElement="div"
                          >
                            <TruncateAndTooltip text={item.Note} />
                          </Text>
                        </>
                      )}
                    </GridCell>
                    <GridCell span={5} data-mu-test="item-url">
                      {loading ? (
                        <SkeletonLoader
                          variant={SKELETON_LOADER_VARIANT_ONWHITE}
                        />
                      ) : (
                        <>
                          {!item.Url && (
                            <Text
                              variant={TEXT_VARIANT_LIGHT}
                              data-tip={intl.formatMessage({
                                id: 'Load.Common.DataUnavailable',
                              })}
                            >
                              <IconWarningTriangle />
                            </Text>
                          )}
                          {item.Url && item.UrlCategoryCode === 'GEN' && (
                            <WordBreakContainer>
                              {item.Url.startsWith('[') ? (
                                <Text variant={TEXT_VARIANT_LIGHT}>
                                  {item.Url}
                                </Text>
                              ) : (
                                <Link
                                  variant={LINK_VARIANT_EXTRALIGHT}
                                  href={item.Url}
                                  target="_blank"
                                  rel="noreferrer noopener"
                                >
                                  {item.Url}
                                </Link>
                              )}
                            </WordBreakContainer>
                          )}

                          {item.Url && item.UrlCategoryCode !== 'GEN' && (
                            <Text variant={TEXT_VARIANT_LIGHT}>
                              {setSpecialLink(item.UrlCategoryCode)}
                            </Text>
                          )}
                        </>
                      )}
                    </GridCell>
                    <GridCell span={2} data-mu-test="item-data">
                      {loading ? (
                        <SkeletonLoader
                          variant={SKELETON_LOADER_VARIANT_ONWHITE}
                        />
                      ) : (
                        <Text
                          variant={TEXT_VARIANT_LIGHT}
                          className="text-light"
                        >
                          {item.EventDate}
                        </Text>
                      )}
                    </GridCell>
                    {hasStatPermission && (
                      <GridCell span={1} data-mu-test="item-actions">
                        <Flex justifyContent="flex-end">
                          <Actions
                            items={getActionItems(item)}
                            iconMenu="icon-action"
                            data-mu-test={
                              'item-actions item-actions_' + item.IdMessage
                            }
                            disabled={loading}
                          />
                        </Flex>
                      </GridCell>
                    )}
                  </Grid>
                </DataListRow>
              ))}
            </section>

            <Pagination />
          </>
        )}
      </Panel>
    </>
  )
}

export default ClickTable
